import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { Collapse, ListItemText } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';

import { fundsSlice, publicOfferSlice } from '!views/index';
import { marketStockExchangeSlice } from '!views/Market/StockExchange/_marketStockExchange.slice';
import { useIsAbleToProceed } from '»utils/customHooks/useIsAbleToProceed';

import { menusSlice } from '../_menus.slice';
import {
  FontAwesomeIconStyled,
  LeafListItemButtonStyled,
  LeafListItemTextStyled,
  ListItemIconStyled,
  ListStyled,
  RootListItemButtonStyled,
} from '../Menu.styles';
import {
  getActiveMenuItemSelector,
  getActiveNavigationMenuItemSelector,
  getMainMenu,
  isMenuOpened,
} from '../menus.selectors';

export const MarketMenuItem = () => {
  const dispatch = useDispatch();

  const activeMenuItem = useSelector(getActiveMenuItemSelector);
  const activeNavigationMenuItem = useSelector(
    getActiveNavigationMenuItemSelector,
  );
  const isOpen = useSelector(isMenuOpened);
  const mainMenu = useSelector(getMainMenu);

  const { isAbleToProceed } = useIsAbleToProceed();

  const available = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET',
  );
  const availablePrimary = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET_PRIMARY',
  );
  const availableStockExchange = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET_STOCK_EXCHANGE',
  );
  const availableFunds = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_MARKET_FUNDS',
  );

  const OPTION_ID = 'MARKET';
  const LEAF_OPTION_ID1 = 'PRIMARY';
  const LEAF_OPTION_ID2 = 'BOLSA';
  const LEAF_OPTION_ID3 = 'FUNDS';

  const handleClickLeafMenu = (event, menuItem) => {
    isAbleToProceed(event, () => {
      if (menuItem !== activeMenuItem) {
        dispatch(
          menusSlice.actions.setActiveMenuItem({ activeMenuItem: menuItem }),
        );

        if (menuItem.includes('BOLSA')) {
          dispatch(marketStockExchangeSlice.actions.setCurrentTab(0));
        } else if (menuItem.includes('PRIMARY')) {
          dispatch(publicOfferSlice.actions.setCurrentTab(0));
        } else {
          dispatch(fundsSlice.actions.setCurrentTab(0));
        }
      }
    });
  };
  const handleClickMenuRoot = (menuItem) => {
    dispatch(
      menusSlice.actions.setActiveNavigationMenuItem({
        activeNavigationMenuItem: menuItem,
      }),
    );
  };

  if (!available) return null;

  return (
    <>
      <RootListItemButtonStyled
        id="btnMenuMarket"
        onClick={() => handleClickMenuRoot(OPTION_ID)}
        selected={activeMenuItem?.includes(OPTION_ID)}
        opened={activeNavigationMenuItem.includes(OPTION_ID)}
        isOpen={isOpen}
      >
        <ListItemIconStyled>
          <FontAwesomeIconStyled
            icon={faChartLine}
            selected={activeMenuItem?.includes(OPTION_ID)}
          />
        </ListItemIconStyled>
        <ListItemText primary={<FormattedMessage id="TRLT_UI-MARKET" />} />
      </RootListItemButtonStyled>
      <Collapse
        in={activeNavigationMenuItem.includes(OPTION_ID)}
        timeout="auto"
        unmountOnExit
      >
        <ListStyled
          component="div"
          disablePadding
          selected={activeMenuItem?.includes(OPTION_ID)}
          opened={activeNavigationMenuItem.includes(OPTION_ID)}
        >
          {availableStockExchange && (
            <LeafListItemButtonStyled
              onClick={(event) => {
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID2}`);
                dispatch(marketStockExchangeSlice.actions.setSelectedTitle({}));
              }}
              component={Link}
              to="/Market/StockExchange"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID2}`,
              )}
              id="btnMenuMarketSecondary"
              isOpen={isOpen}
            >
              <LeafListItemTextStyled
                primary={
                  isOpen ? (
                    <FormattedMessage id="TRLT_UI-EXCHANGE" />
                  ) : (
                    <FormattedMessage id="TRLT_UI-EXCHANGE_SHORT" />
                  )
                }
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID2}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonStyled>
          )}
          {availablePrimary && (
            <LeafListItemButtonStyled
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID1}`)
              }
              component={Link}
              to="/Market/Primary"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID1}`,
              )}
              id="btnMenuMarketPrimary"
              isOpen={isOpen}
            >
              <LeafListItemTextStyled
                primary={
                  isOpen ? (
                    <FormattedMessage id="TRLT_UI-PRIMARY" />
                  ) : (
                    <FormattedMessage id="TRLT_UI-PRIMARY_SHORT" />
                  )
                }
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID1}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonStyled>
          )}
          {availableFunds && (
            <LeafListItemButtonStyled
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID3}`)
              }
              component={Link}
              to="/Market/Funds"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID3}`,
              )}
              id="btnMenuMarketFunds"
              isOpen={isOpen}
            >
              <LeafListItemTextStyled
                primary={<FormattedMessage id="TRLT_UI-FUNDS" />}
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID3}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonStyled>
          )}
        </ListStyled>
      </Collapse>
    </>
  );
};

MarketMenuItem.propTypes = {
  isOpen: PropTypes.bool,
  activeMenuItem: PropTypes.string,
  available: PropTypes.bool,
  availablePrimary: PropTypes.bool,
  availableStockExchange: PropTypes.bool,
  availableFunds: PropTypes.bool,
  setActiveMenuItem: PropTypes.func,
  activeNavigationMenuItem: PropTypes.string,
  setActiveNavigationMenuItem: PropTypes.func,
  setSelectedTitle: PropTypes.func,
};
